/**
 * What the user is looking for — the search.
 *
 * These filters are obeyed exactly — we never widen a search to find something
 * to apply to. The copy says so plainly, because the whole reason people
 * distrust this category of tool is that it applies to things they would not
 * have picked.
 *
 * Auto-apply (mode, rules, connect-this-browser, daily limit) lives on its own
 * screen now — see AutoApply.tsx. This screen and that one both load and save
 * the whole preferences row, so each preserves the other's fields.
 */

import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "./auth";
import { NextStep, type NextStepInfo } from "./NextStep";
import { TagField, CheckGroup, MoneyField, ErrorList } from "./fields";

export interface Preferences {
  label: string;
  titles: string[];
  excludeTitles: string[];
  metros: string[];
  modality: string[];
  compFloorCents: number | null;
  seniority: string[];
  industries: string[];
  applyMode: "review" | "auto";
  dailyCap: number;
  autoMinScore: number;
  autoMaxAgeDays: number;
  autoStatedPayOnly: boolean;
  autoCloud: boolean;
  /** Whether matches include jobs the seeker applies to themselves (the Manual type). */
  includeManual: boolean;
}

const MODALITY_OPTIONS = [
  { value: "remote", label: "Remote" },
  { value: "hybrid", label: "Hybrid" },
  { value: "onsite", label: "On site" },
] as const;

export default function Preferences({ onSaved, onBack, next, onGo, savedBefore }: { onSaved: () => void; onBack: () => void; next: NextStepInfo; onGo: (go: string) => void; savedBefore: boolean }) {
  const [prefs, setPrefs] = useState<Preferences | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  /**
   * `loading` is tracked separately from `prefs` on purpose. Gating the render
   * on `prefs === null` means a failed load leaves the screen showing "Loading…"
   * forever with the real error set in state and never displayed — the same
   * class of bug as swallowing Auth0's ?error= redirect.
   */
  const load = useCallback(() => {
    setLoading(true);
    setErrors([]);
    api<{ preferences: Preferences }>("/api/preferences")
      .then((r) => setPrefs(r.preferences))
      .catch((e) => setErrors(e instanceof ApiError ? e.messages : [(e as Error).message]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  if (loading) return <p className="muted">Loading…</p>;

  if (!prefs) {
    return (
      <>
        <header className="bar">
          <h1 className="tight">Couldn't load your search</h1>
          <button className="btn ghost small" onClick={onBack}>Back</button>
        </header>
        <ErrorList errors={errors} />
        <p className="muted small">
          If this says 404, the API server is running older code — restart{" "}
          <code>wrangler dev</code>.
        </p>
        <div className="row"><button className="btn" onClick={load}>Try again</button></div>
      </>
    );
  }

  const set = <K extends keyof Preferences>(k: K, v: Preferences[K]) => {
    setPrefs({ ...prefs, [k]: v });
    setSavedAt(null);
  };

  const save = async () => {
    setSaving(true);
    setErrors([]);
    try {
      // The whole row is sent, so the fields set on Auto-apply are preserved.
      const r = await api<{ preferences: Preferences }>("/api/preferences", {
        method: "PUT",
        body: JSON.stringify(prefs),
      });
      setPrefs(r.preferences);
      setSavedAt(Date.now());
      onSaved();
    } catch (e) {
      setErrors(e instanceof ApiError ? e.messages : [(e as Error).message]);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <header className="bar">
        <div>
          <h1 className="tight">What you're looking for</h1>
          <p className="muted small">We obey these exactly. No widening to fill a quota.</p>
        </div>
        <button className="btn ghost small" onClick={onBack}>Back</button>
      </header>

      <TagField
        label="Job titles"
        hint="Type one and press Enter. Add the variations employers actually use — “Test Manager” and “QA Manager” are different postings."
        values={prefs.titles}
        onChange={(v) => set("titles", v)}
        placeholder="Test Manager"
      />

      <TagField
        label="Titles to skip"
        hint="Anything that keeps showing up and is never right."
        values={prefs.excludeTitles}
        onChange={(v) => set("excludeTitles", v)}
        placeholder="Sales Engineer"
      />

      <TagField
        label="Locations"
        hint="Metro areas. Leave empty if you only want remote."
        values={prefs.metros}
        onChange={(v) => set("metros", v)}
        placeholder="Nashville, TN"
      />

      <CheckGroup
        label="Work arrangement"
        options={MODALITY_OPTIONS}
        values={prefs.modality}
        onChange={(v) => set("modality", v)}
      />

      <MoneyField
        label="Pay floor"
        hint="Base salary. We only filter on pay the employer actually published — never on an estimate."
        cents={prefs.compFloorCents}
        onChange={(c) => set("compFloorCents", c)}
      />

      <TagField
        label="Seniority"
        values={prefs.seniority}
        onChange={(v) => set("seniority", v)}
        placeholder="Senior"
      />

      <TagField
        label="Industries"
        values={prefs.industries}
        onChange={(v) => set("industries", v)}
        placeholder="Healthcare"
      />

      <h2>What shows in your matches</h2>
      <label className="choice">
        <input type="checkbox" checked={prefs.includeManual} onChange={(e) => set("includeManual", e.target.checked)} />
        <span>
          Show jobs you apply to yourself{" "}
          <span className="muted small">
            (Workday, employer sites and other places JobCork can't auto-fill — you apply on their page and mark it applied here.
            On by default; turning it off narrows your matches to only the jobs JobCork can fill.)
          </span>
        </span>
      </label>

      <ErrorList errors={errors} />

      <div className="row">
        <button className="btn" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
        {savedAt && <span className="muted small saved">Saved</span>}
      </div>

      <NextStep ready={savedAt !== null || savedBefore} next={next} onGo={onGo} onBack={onBack} doneWords={savedAt ? "Search saved" : "Search set"} />
    </>
  );
}
